import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, TextField, Button, Alert,
  Chip, Stack, Skeleton, Dialog, DialogContent, DialogActions,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  AccountBalance as LoanIcon,
  Savings as SavingsIcon,
} from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { formatCurrency } from '@/utils/format';
import { useJurisdiction } from '@/hooks/useJurisdiction';
import { useNotificationStore } from '@/stores/notificationStore';
import { portalFinanceApi, LoanRequestCreate } from '@/api/endpoints/hr';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import { ConfirmDialog } from '@/components/ConfirmDialog';

export default function MyFinancesTab() {
  const { currencyCode } = useJurisdiction();
  const { showSuccess, showError } = useNotificationStore();
  const qc = useQueryClient();
  const [loanFormOpen, setLoanFormOpen] = useState(false);
  const [loanForm, setLoanForm] = useState<LoanRequestCreate>({ amount_requested: 0, proposed_monthly_repayment: 0, purpose: '' });
  const [cancelLoanId, setCancelLoanId] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['my-finances'],
    queryFn: () => portalFinanceApi.getMyFinances(),
    select: (res) => res.data,
    retry: false,
  });

  // The my-finances endpoint 404s when the signed-in user has no employee
  // record. Surface clear guidance rather than rendering empty KPIs and a
  // "Request Loan" button that would always fail with "Employee record not found".
  const noEmployeeRecord = isError && (error as any)?.response?.status === 404;

  const submitLoanMutation = useMutation({
    mutationFn: () => portalFinanceApi.submitLoanRequest(loanForm),
    onSuccess: () => {
      showSuccess('Loan request submitted — you will be notified when it is reviewed');
      setLoanFormOpen(false);
      setLoanForm({ amount_requested: 0, proposed_monthly_repayment: 0, purpose: '' });
      qc.invalidateQueries({ queryKey: ['my-finances'] });
    },
    onError: (e: any) => showError(e.response?.data?.detail || 'Failed to submit loan request'),
  });

  const cancelLoanMutation = useMutation({
    mutationFn: (id: string) => portalFinanceApi.cancelLoanRequest(id),
    onSuccess: () => {
      showSuccess('Loan request cancelled');
      qc.invalidateQueries({ queryKey: ['my-finances'] });
    },
    onError: (e: any) => showError(e.response?.data?.detail || 'Failed to cancel request'),
  });

  const deductions = data?.deductions || [];
  const loanRequests = data?.loan_requests || [];
  const activeDeductions = deductions.filter((d) => d.is_active && d.deduction_type !== 'savings');
  const savingsPlans = deductions.filter((d) => d.deduction_type === 'savings');
  const totalSavings = savingsPlans.reduce((s, d) => s + Number(d.accumulated_amount || 0), 0);
  const monthlyDeductions = activeDeductions.reduce((s, d) => s + Number(d.monthly_amount || 0), 0);
  const installments = loanForm.proposed_monthly_repayment > 0 ? Math.ceil(loanForm.amount_requested / loanForm.proposed_monthly_repayment) : 0;

  // Loan policy (BUG-063) — mirror the server-side caps so staff get guidance
  // and blocked submits before the request round-trips.
  const loanPolicy = data?.loan_policy;
  const maxLoan = loanPolicy?.max_loan_amount ?? 0;
  const loanTermMonths = loanPolicy?.max_loan_term_months ?? 24;
  const loanSalaryMultiple = loanPolicy?.max_loan_salary_multiple ?? 6;
  const minRepayment = loanForm.amount_requested > 0 ? Math.ceil(loanForm.amount_requested / loanTermMonths) : 0;
  const noSalaryForLoan = loanPolicy !== undefined && maxLoan <= 0;
  const amountOverCap = maxLoan > 0 && loanForm.amount_requested > maxLoan;
  const repaymentOverAmount = loanForm.amount_requested > 0 && loanForm.proposed_monthly_repayment > loanForm.amount_requested;
  const repaymentTooLow = loanForm.amount_requested > 0 && loanForm.proposed_monthly_repayment > 0 && loanForm.proposed_monthly_repayment < minRepayment;
  const loanFormInvalid = noSalaryForLoan || amountOverCap || repaymentOverAmount || repaymentTooLow;

  if (isLoading) return <Skeleton variant="rectangular" height={200} />;

  if (noEmployeeRecord) {
    return (
      <Card sx={{ textAlign: 'center', py: 6 }}>
        <LoanIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
        <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.semibold, mb: 1 }}>No Employee Record Found</Typography>
        <Typography sx={{ fontSize: TYPE.base, color: 'text.secondary', maxWidth: 420, mx: 'auto' }}>
          Savings, deductions, and loan requests require an employee record. Ask an
          administrator to add you as an employee in HR &rarr; Employees using your work email.
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      {/* KPI Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card variant="outlined" sx={{ borderTop: '3px solid', borderTopColor: 'success.main' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SavingsIcon sx={{ fontSize: 20, color: 'success.main' }} />
                <Typography variant="caption" color="text.secondary">Total Savings</Typography>
              </Stack>
              <Typography variant="h6" fontWeight={TYPE.bold} color="success.main" sx={{ fontSize: '1.3rem' }}>{formatCurrency(totalSavings, currencyCode)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card variant="outlined" sx={{ borderTop: '3px solid', borderTopColor: 'warning.main' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Active Deductions</Typography>
              <Typography variant="h6" fontWeight={TYPE.bold} sx={{ fontSize: '1.3rem' }}>{activeDeductions.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card variant="outlined" sx={{ borderTop: '3px solid', borderTopColor: 'info.main' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Monthly Deductions</Typography>
              <Typography variant="h6" fontWeight={TYPE.bold} sx={{ fontSize: '1.3rem' }}>{formatCurrency(monthlyDeductions, currencyCode)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card variant="outlined" sx={{ borderTop: '3px solid', borderTopColor: 'primary.main' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Pending Loans</Typography>
              <Typography variant="h6" fontWeight={TYPE.bold} sx={{ fontSize: '1.3rem' }}>{loanRequests.filter((r) => r.status === 'pending').length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Savings Section */}
      {savingsPlans.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={TYPE.semibold} sx={{ mb: 1.5 }}>
              <SavingsIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom', color: 'success.main' }} />
              My Savings
            </Typography>
            {savingsPlans.map((s) => (
              <Box key={s.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box>
                  <Typography variant="body2" fontWeight={TYPE.medium}>{s.description || 'Staff Savings'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {s.calculation_method === 'percentage' ? `${s.percentage}% of basic salary` : `${formatCurrency(s.monthly_amount, currencyCode)} / month`}
                    {s.is_active ? '' : ' (paused)'}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body1" fontWeight={TYPE.bold} color="success.main">{formatCurrency(s.accumulated_amount, currencyCode)}</Typography>
                  <Typography variant="caption" color="text.secondary">accumulated</Typography>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Active Deductions */}
      {activeDeductions.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={TYPE.semibold} sx={{ mb: 1.5 }}>
              <LoanIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom', color: 'primary.main' }} />
              Active Deductions
            </Typography>
            {activeDeductions.map((d) => (
              <Box key={d.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box>
                  <Typography variant="body2" fontWeight={TYPE.medium}>
                    {d.description || d.deduction_type.charAt(0).toUpperCase() + d.deduction_type.slice(1)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(d.monthly_amount, currencyCode)} / month • {d.installments_remaining ?? 0} periods left
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2">{formatCurrency(d.remaining_balance, currencyCode)} remaining</Typography>
                  <Typography variant="caption" color="text.secondary">of {formatCurrency(d.total_amount, currencyCode)}</Typography>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {activeDeductions.length === 0 && savingsPlans.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>No active deductions or savings plans.</Alert>
      )}

      {/* Loan Requests */}
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={TYPE.semibold}>
              Loan Requests
            </Typography>
            <Button variant="contained" size="small" onClick={() => setLoanFormOpen(true)}>
              Request Loan
            </Button>
          </Stack>
          {loanRequests.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No loan requests yet. Click "Request Loan" to apply.
            </Typography>
          ) : (
            loanRequests.map((lr) => (
              <Box key={lr.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box>
                  <Typography variant="body2" fontWeight={TYPE.medium}>{lr.purpose}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrency(lr.amount_requested, currencyCode)} • {lr.proposed_monthly_repayment ? formatCurrency(lr.proposed_monthly_repayment, currencyCode) + '/month' : ''}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={lr.status.charAt(0).toUpperCase() + lr.status.slice(1)}
                    size="small"
                    color={lr.status === 'pending' ? 'warning' : lr.status === 'disbursed' ? 'success' : lr.status === 'rejected' ? 'error' : 'default'}
                    variant="outlined"
                  />
                  {lr.status === 'pending' && (
                    <Button size="small" color="error" variant="text"
                      onClick={() => setCancelLoanId(lr.id)}>Cancel</Button>
                  )}
                </Stack>
              </Box>
            ))
          )}
        </CardContent>
      </Card>

      {/* Submit Loan Request Dialog */}
      <Dialog open={loanFormOpen} onClose={() => setLoanFormOpen(false)} maxWidth="sm" fullWidth>
        <BrandedDialogTitle>Request a Loan</BrandedDialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={2.5}>
            <Alert severity="info" icon={false}>
              Your request will be reviewed by HR/Finance. If approved, repayments will be automatically deducted from your salary each month.
            </Alert>
            {noSalaryForLoan ? (
              <Alert severity="warning" icon={false}>
                A loan requires a configured salary. Your basic salary isn't set yet, so a loan amount can't be calculated — please ask HR to set your salary first.
              </Alert>
            ) : maxLoan > 0 && (
              <Alert severity="info" icon={false}>
                You can request up to {formatCurrency(maxLoan, currencyCode)} ({loanSalaryMultiple}× your monthly basic salary), repayable within {loanTermMonths} months.
              </Alert>
            )}
            <TextField
              size="small" label="Amount Requested" type="number" required fullWidth
              value={loanForm.amount_requested || ''}
              onChange={(e) => setLoanForm({ ...loanForm, amount_requested: Number(e.target.value) || 0 })}
              inputProps={{ min: 1000, step: 1000 }}
              error={amountOverCap}
              helperText={
                amountOverCap
                  ? `Exceeds the maximum of ${formatCurrency(maxLoan, currencyCode)} (${loanSalaryMultiple}× your monthly basic salary)`
                  : maxLoan > 0
                  ? `Maximum ${formatCurrency(maxLoan, currencyCode)}`
                  : ''
              }
            />
            <TextField
              size="small" label="Proposed Monthly Repayment" type="number" required fullWidth
              value={loanForm.proposed_monthly_repayment || ''}
              onChange={(e) => setLoanForm({ ...loanForm, proposed_monthly_repayment: Number(e.target.value) || 0 })}
              inputProps={{ min: 500, step: 500 }}
              error={repaymentTooLow || repaymentOverAmount}
              helperText={
                repaymentOverAmount
                  ? 'Monthly repayment cannot exceed the loan amount'
                  : repaymentTooLow
                  ? `Too low — must repay within ${loanTermMonths} months (minimum ${formatCurrency(minRepayment, currencyCode)}/month)`
                  : minRepayment > 0
                  ? `Minimum ${formatCurrency(minRepayment, currencyCode)}/month · ${installments > 0 ? `${installments} installments` : ''}`
                  : installments > 0 ? `${installments} installments` : ''
              }
            />
            <TextField
              size="small" label="Purpose / Reason" required fullWidth multiline rows={3}
              value={loanForm.purpose}
              onChange={(e) => setLoanForm({ ...loanForm, purpose: e.target.value })}
              placeholder="Explain why you need this loan..."
              error={loanForm.purpose.length > 0 && loanForm.purpose.length < 10}
              helperText={
                loanForm.purpose.length === 0
                  ? 'Minimum 10 characters'
                  : loanForm.purpose.length < 10
                  ? `${10 - loanForm.purpose.length} more character${10 - loanForm.purpose.length === 1 ? '' : 's'} needed`
                  : `${loanForm.purpose.length} / 1000`
              }
            />
            {loanForm.amount_requested > 0 && loanForm.proposed_monthly_repayment > 0 && (
              <Alert severity="info" icon={false}>
                <Typography variant="body2">
                  {formatCurrency(loanForm.proposed_monthly_repayment, currencyCode)} will be deducted monthly for {installments} months (total: {formatCurrency(loanForm.amount_requested, currencyCode)}).
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoanFormOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => submitLoanMutation.mutate()}
            disabled={submitLoanMutation.isPending || loanForm.amount_requested <= 0 || loanForm.proposed_monthly_repayment <= 0 || loanForm.purpose.length < 10 || loanFormInvalid}
          >
            {submitLoanMutation.isPending ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={!!cancelLoanId}
        title="Cancel this loan request?"
        description="The request will be withdrawn and cannot be resubmitted."
        confirmLabel="Cancel request"
        confirmColor="error"
        onConfirm={() => { if (cancelLoanId) cancelLoanMutation.mutate(cancelLoanId); }}
        onClose={() => setCancelLoanId(null)}
      />
    </Box>
  );
}
